import {
  AfterViewInit,
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ChangeDetectionStrategy
} from '@angular/core';
import {Community, ProfileQuestion, QuestionAnswer} from "../../_types/communities";
import {AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, Validators} from "@angular/forms";
import {LocalStorageService} from "../../_services/local-storage.service";
import {AuthService} from "../../_services/auth.service";
import {SnackbarService} from "../../_services/snackbar.service";
import {CommunitiesService} from "../../_services/communities.service";
import {Router} from "@angular/router";
import {startWith, take} from "rxjs";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {ThemeService} from "../../_services/theme.service";

@Component({
    selector: 'app-profile-questions',
    templateUrl: './profile-questions.component.html',
    styleUrl: './profile-questions.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ProfileQuestionsComponent implements OnChanges, AfterViewInit {
    @Input() community: Community | undefined | null = this.selectedCommunity;
    @Input() showHeader: boolean = true;
    @Input() card: boolean = true;
    @Input() redirect: boolean = true;
    @Input() isRegistrationPage: boolean = false;

    @Output() storeProfileAnswers: EventEmitter<QuestionAnswer[]> = new EventEmitter<QuestionAnswer[]>();

    public profileForm: FormGroup<{
        [key: string]: FormControl<string | Date | boolean | null>
    }>;
    public dynamicFormFields: ProfileQuestion[] = [];
    public loading: boolean = true;
    public companyImg: string = localStorage.getItem('communityLogo') || 'assets/explainLogoPlaceholder.png';
    public companyName: string = localStorage.getItem('communityName') || 'Explain'
    public today: Date = new Date();

    private isCompanyAlreadyLoaded: boolean = false;
    private answersByQuestion: Map<number, any> = new Map<number, any>();


    constructor(
        private readonly fb: FormBuilder,
        private readonly localStorageService: LocalStorageService,
        private readonly authService: AuthService,
        private readonly snackbarService: SnackbarService,
        private readonly communitiesService: CommunitiesService,
        private readonly router: Router,
        private readonly destroyRef: DestroyRef,
        private themeService: ThemeService
    ) {
        this.profileForm = this.fb.group({});
    }

    get selectedCommunity(): Community | null {
        return this.localStorageService.getItem<Community | null>('lastSelectedCommunity', null);
    }

    getControl(id: any): AbstractControl | null {
        return this.profileForm.get(String(id));
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['community']?.currentValue) {
            this.tryReloadCommunityData();
        }
    }

    ngAfterViewInit(): void {
        if (!this.isCompanyAlreadyLoaded) {
            this.tryReloadCommunityData();
        }
    }

    setFieldVisability(id: number, hide: boolean): void {
        const field = this.dynamicFormFields.find(q => q.id === id);
        if (!field) return;

        field.hidden = hide;

        if (hide) {
            this.profileForm.get(String(id))?.setValue(null, {emitEvent: false});
            field.incorrectAge = false;
        }

        this.syncQuestionValidator(field);
    }

    shuffleArray(array: any) {
        for (let i: number = array.length - 1; i > 0; i--) {
            const j: number = Math.floor(Math.random() * (i + 1));
            [array[i], array[j]] = [array[j], array[i]];
        }
        return array;
    }

    randomize(arr: any) {
        // Step 1: Find the indices of objects that have fixed false
        let matchingIndices = arr.reduce((acc: any, obj: any, index: number) => {
            if (obj['random_order']) {
                acc.push(index);
            }
            return acc;
        }, []);

        // Step 2: Extract matching objects based on their indices
        let matchingObjects = matchingIndices.map((index: number) => arr[index]);

        // Step 3: Shuffle the extracted objects
        matchingObjects = this.shuffleArray(matchingObjects);

        // Step 4: Put the shuffled objects back in their original positions
        matchingIndices.forEach((index: number, i: number): void => {
            arr[index] = matchingObjects[i];
        });

        return arr;
    }

    fieldSelect(field: any, option: any): void {
        if (field.selectionChangeInProgress) {
            return; // Prevent recursive calls
        }
        field.selectionChangeInProgress = true;
        try {
            // During multiple choice on value changes check that the option isnt exclusive.
            let mostRecentlySelected
            let formControl: AbstractControl<any, any> | null = this.profileForm.get(field.id.toString());
            let currentValue: any[] = formControl?.value || [];
            if (field.question_type == 'dropdown' && field.dropdown_allows_multiple_selection && option.source.value) {
                mostRecentlySelected = option.source.value;
            } else {
                mostRecentlySelected = option.id;
            }

            const i: number = currentValue?.findIndex((x: any): boolean => x === option.id);
            // if  the id isnt already in the array then pass the value
            if (i < 0) {
                let tempValue = currentValue?.concat(option.id)
                formControl?.setValue(tempValue)
            } else {
                let tempValue = currentValue
                // if the id is already in the array then remove the value
                tempValue.splice(i, 1);
                formControl?.setValue(tempValue)
            }

            for (let fieldOption of field.options) {
                if (((formControl!.value?.includes(fieldOption.id) && field.question_type == 'multiple_choice') || (field.question_type == 'dropdown' && field.dropdown_allows_multiple_selection)) && fieldOption.is_exclusive) {
                    let tempValue = formControl?.value
                    // If option is exclusive remove other options and just set this one

                    if (fieldOption.id == mostRecentlySelected) {
                        // and exclusive has been selected so remove others
                        formControl!.setValue([fieldOption.id]);
                    } else {
                        //  an exclusive pre-exists so must be removed.
                        const i: number = tempValue?.findIndex((x: any): boolean => x === fieldOption.id);

                        tempValue.splice(i, 1);
                        formControl?.setValue(tempValue)
                    }
                }
            }
        } finally {
            field.selectionChangeInProgress = false;
        }
    }

    checkIfRequired(id: number): boolean {
        return this.profileForm.get(String(id))?.hasValidator(Validators.required)!;
    }

    checkIfCorrectAge(field: any, value: Date): void {
        if (field.minimum_number_of_years) {
            if (value) {
                const ageDifMs: number = Date.now() - value.getTime();
                const ageDate: Date = new Date(ageDifMs); // miliseconds from epoch
                let years: number = Math.abs(ageDate.getUTCFullYear() - 1970);
                field.incorrectAge = years < field.minimum_number_of_years
            } else {
                field.incorrectAge = true;
            }
        }
    }

    allAgesCorrect(): boolean {
        for (let question of this.dynamicFormFields) {
            // Only validate visible questions
            if (!question.hidden && question.incorrectAge) {
                return false;
            }
        }
        return true;
    }

    formatQuestions(): QuestionAnswer[] {
        let answers: QuestionAnswer[] = [];
        for (let field of this.dynamicFormFields) {
            if (!field.question_type || field.hidden) continue;
            let answer = this.profileForm.get(field.id.toString())!.value;
            if (answer instanceof Date) {
                answer = answer.toLocaleDateString('en-CA').slice(0, 10);
            }
            if (answer == '') {
                answer = null;
            }
            switch (field.question_type) {
                case "single_choice":
                    answers.push({
                        question: field.id,
                        options: [answer]
                    })
                    break;
                case  "dropdown":
                    if (field.dropdown_allows_multiple_selection) {
                        answers.push({
                            question: field.id,
                            options: answer
                        })
                    } else {
                        answers.push({
                            question: field.id,
                            options: [answer]
                        })
                    }
                    break;
                case "multiple_choice":
                    answers.push({
                        question: field.id,
                        options: answer
                    })
                    break;
                case "date":
                    answers.push({
                        question: field.id,
                        answer_date: answer
                    })
                    break;
                case "text":
                    answers.push({
                        question: field.id,
                        answer_text: answer
                    })
                    break;
                case "numeric":
                    answers.push({
                        question: field.id,
                        answer_text: answer
                    })
                    break;
            }
        }
        return answers
    }

    submit(): void {
        // first profile submit
        let answers: QuestionAnswer[] = this.formatQuestions();
        this.storeProfileAnswers.emit(answers);
    }

    update(): void {
        this.loading = true;
        // update profile
        let answers: QuestionAnswer[] = this.formatQuestions();
        this.sendProfileQuestions(this.authService.getUser()?.id, answers).then((): void => {
            this.reCheckUserFinishRegistration();
        }).catch((): void => {
            this.loading = false;
        })
    }

    reCheckUserFinishRegistration(): void {
        this.communitiesService.getUserInformationForCommunity(this.community!.id).subscribe(res => {
            this.communitiesService.profileRefreshed = !res ? true : (res?.profile_refreshed === null || !!res?.profile_refreshed);
            if (this.communitiesService.profileRefreshed || !this.community?.questions?.length) {
                if (this.isRegistrationPage) {
                    this.snackbarService.openSuccessSnackBar('You successfully finished onboarding');

                } else {
                    this.snackbarService.openSuccessSnackBar('You successfully updated your profile');
                }
                if (this.redirect) {
                    this.router.navigate(['/feed']);
                }
                this.loading = false;
                return;
            } else {
                this.loading = false;
                this.snackbarService.openErrorSnackBar('Onboarding in selected community not finished');
                window.location.reload();
            }
        }, e => {
            this.loading = false;
            this.snackbarService.openErrorSnackBar(e);
            window.location.reload();
        })
    }

    sendProfileQuestions(userId: number, answers: QuestionAnswer[]) {
        return new Promise((resolve, reject): void => {
            let done: number = 0;
            if (answers.length == 0) return resolve([])
            for (let answer of answers) {
                (answer as any)['user'] = userId;
                if (answer.options && answer.options[0] == null) {
                    answer.options = [];
                }
                this.authService.postProfileQuestionAnswer(answer).pipe(take(1)).subscribe(r => {
                    done++
                    if (done == answers.length) {
                        resolve(r)
                    }
                }, e => {
                    reject(e)
                    this.snackbarService.openErrorSnackBar(e);
                })
            }
        });
    }

    validateForm(formGroup: FormGroup | FormArray, parentKey: string = ''): void {
        Object.keys(formGroup.controls).forEach(key => {
            const control = formGroup.get(key);
            const controlPath = parentKey ? `${parentKey}.${key}` : key;

            if (control instanceof FormGroup || control instanceof FormArray) {
                this.validateForm(control, controlPath);  // Recursive call
            } else if (control && control.invalid) {
                console.log(`❌ Invalid field: ${controlPath}`);
                const q = this.dynamicFormFields.find(x => String(x.id) == controlPath);
                console.log('  🔸 Errors:', control.errors);
            }
        });
    }

    private syncQuestionValidator(question: ProfileQuestion): void {
        const control = this.profileForm.get(String(question.id));
        if (!control) return;

        if (question.required && !question.hidden) {
            control.setValidators(Validators.required);
        } else {
            control.clearValidators();
        }

        control.updateValueAndValidity({emitEvent: false});
    }

    private hasAnswer(questionId: number): boolean {
        const answer = this.answersByQuestion.get(questionId);
        if (!answer) return false;

        return Boolean(
            answer.answer_date ||
            (typeof answer.answer_text === 'string' && answer.answer_text.trim().length > 0) ||
            (answer.answer_text != null && typeof answer.answer_text !== 'string') ||
            (Array.isArray(answer.options) && answer.options.length > 0)
        );
    }

    private shouldAsk(q: ProfileQuestion): boolean {
        // Ask if requires_refresh OR (required and unanswered)
        return !!q.requires_refresh || (!!q.required && !this.hasAnswer(q.id));
    }


    private filterQuestions(questions: ProfileQuestion[]): Promise<ProfileQuestion[]> {
        return new Promise((resolve, reject): void => {
            if (this.isRegistrationPage) {
                for (const question of questions) {
                    const isChild: boolean =
                        !!question.parent_question ||
                        (Array.isArray(question.parent_options) && question.parent_options.length > 0);

                    question.hidden = isChild; // parents visible, children hidden until triggered
                }
                resolve(questions);
                return;
            }


            if (this.authService.getUser() && this.community?.id) {
                this.authService.getProfileQuestionAnswer(this.community?.id).subscribe(r => {
                    this.answersByQuestion.clear();
                    //  hide fields here if they have an answer and don't have requires_refresh
                    for (const answer of (r?.results ?? [])) {
                        this.answersByQuestion.set(answer.question, answer);
                    }

                    for (const question of questions) {
                        const isChild: boolean =
                            !!question.parent_question ||
                            (Array.isArray(question.parent_options) && question.parent_options.length > 0);

                        if (isChild) {
                            question.hidden = true;
                            continue;
                        }

                        const answer = this.answersByQuestion.get(question.id);
                        const hasAnswer =
                            !!answer && (answer.answer_date || answer.answer_text || (Array.isArray(answer.options) && answer.options.length > 0));

                        const shouldShow: boolean =
                            !!question.requires_refresh || (!!question.required && !hasAnswer);

                        question.hidden = !shouldShow;
                    }

                    resolve(questions);
                }, e => {
                    reject(e);
                    console.error(e);
                });
            } else {
                resolve(questions);
            }
        });
    }

    private tryReloadCommunityData(): void {
        if (this.community?.id) {
            this.loading = true;
            this.isCompanyAlreadyLoaded = true; // This flag only for skip initial loading (for separate page logic)
            this.companyImg = this.community.logo;
            this.companyName = this.community.name;
            if (this.community?.main_color_hex) {
                this.themeService.savePrimaryColor(this.community?.main_color_hex)
            }
            if (this.community?.accent_color_hex) {
                this.themeService.saveAccentColor(this.community?.accent_color_hex)
            }
            this.authService.getCommunityProfileQuestions(this.community?.id).pipe(take(1)).subscribe(response => {

                this.filterQuestions(response.sort((a, b) => a.order - b.order)).then(res => {
                    const fields: ProfileQuestion[] = this.randomize(res);
                    this.profileForm = this.fb.group({});
                    let parentQuestions: {
                        id: number,
                        children: [{ id: number, options?: number[] | null, required?: boolean }]
                    }[] = [];
                    for (let response of fields) {
                        if (response.question_type) {
                            let control = new FormControl('');
                            this.profileForm.addControl(`${response.id}`, control);
                            this.syncQuestionValidator(response);

                            if (response.options && response.options.length > 0) {
                                this.randomize(response.options)
                            }

                            if (response.parent_question || response.parent_options?.length) {
                                const i: number = parentQuestions.findIndex((x: any): boolean => x.id === response.parent_question);
                                if (i != null && i >= 0) {
                                    parentQuestions[i].children.push({
                                        id: response.id,
                                        options: response.parent_options ?? null,
                                        required: response.required
                                    })
                                } else {
                                    parentQuestions.push({
                                        id: response.parent_question,
                                        children: [{
                                            id: response.id,
                                            options: response.parent_options ?? null,
                                            required: response.required
                                        }]
                                    })
                                }
                            }
                            if (response.minimum_number_of_years) {
                                response.incorrectAge = !response.hidden;
                            }
                        }
                    }

                    this.dynamicFormFields = fields;

                    for (let parentQuestion of parentQuestions) {
                        // When parent question changes
                        this.profileForm.get(String(parentQuestion.id))?.valueChanges
                            .pipe(startWith(''), takeUntilDestroyed(this.destroyRef))
                            .subscribe((res): void => {
                                for (let child of parentQuestion.children) {
                                    const childQuestion: ProfileQuestion | undefined = this.dynamicFormFields.find((x: any) => x.id === child.id);
                                    const shouldAskChild: boolean = this.isRegistrationPage ? true : (childQuestion ? this.shouldAsk(childQuestion) : true);

                                    const shouldShowByParent = (() => {
                                        const opts = child.options;
                                        if (!opts || opts.length === 0) return !!res;
                                        if (Array.isArray(res)) return opts.some(o => res.includes(o));
                                        return opts.includes(res);
                                    })();

                                    if (shouldShowByParent) {
                                        // Parent option and parent question option matches then display child
                                        if (!shouldAskChild) {
                                            // Child already answered and does not require refresh → keep hidden
                                            this.setFieldVisability(child.id, true);
                                            if (childQuestion?.minimum_number_of_years) {
                                                childQuestion.incorrectAge = false;
                                            }
                                            continue;
                                        }

                                        this.setFieldVisability(child.id, false);
                                        if (childQuestion?.minimum_number_of_years && !this.profileForm.get(String(child.id))?.value) {
                                            childQuestion.incorrectAge = true
                                        }
                                    } else {
                                        // hide if already answered and no refresh required
                                        this.setFieldVisability(child.id, true);
                                        if (childQuestion?.minimum_number_of_years) {
                                            childQuestion.incorrectAge = false;
                                        }
                                    }
                                }
                            });
                    }
                })

                if (!this.isRegistrationPage && this.authService.getUser() && this.community?.id) {
                    this.authService.getProfileQuestionAnswer(this.community?.id).subscribe(r => {
                        for (let answer of r.results) {
                            // Find the matching question
                            const qIndex: number = this.dynamicFormFields.findIndex((x: any): boolean => x.id === answer.question);
                            if (qIndex < 0) continue;

                            const question: ProfileQuestion = this.dynamicFormFields[qIndex];

                            // Skip anything that requires refresh
                            if (question.requires_refresh) {
                                continue;
                            }

                            let value = answer.answer_date || answer.answer_text || answer.options;

                            if (Array.isArray(value)) {
                                if (question.question_type !== 'multiple_choice' &&
                                    (question.question_type !== 'dropdown' || !question.dropdown_allows_multiple_selection)) {
                                    value = value[0];
                                }
                            } else if (answer.answer_date) {
                                value = new Date(value);
                            }

                            this.profileForm.get(String(answer.question))?.setValue(value);
                            this.profileForm.get(String(answer.question))?.updateValueAndValidity({emitEvent: false});

                            if (answer.answer_date && question.minimum_number_of_years) {
                                this.checkIfCorrectAge(question, value);
                            }
                        }
                    }, e => {
                        console.error(e)
                    })
                }
                this.loading = false;
            }, e => {
                this.snackbarService.openErrorSnackBar('Error receiving community onboarding questions please try again later');
            })
        } else {
            this.loading = false;
        }
    }
}
